import { environment } from "environments/environment";
import { Component, OnDestroy, OnInit } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { TicketStatuses } from "app/enums/TicketStatuses.enum";
import { ITicket } from "app/interfaces/i-ticket";
import { GoogleAnalyticsService } from "app/services/analytics/google-analitics-service";
import { ApiTicketService } from "app/services/api/api-ticket.service";
import { LocalStorageService } from "app/services/local-storage/local-storage.service";
import { Subscription, merge, of } from "rxjs";
import { NotificationsService } from "angular2-notifications";
import { DialogConfirm } from "../dialog-confirm/dialog-confirm.component";
import { MatDialog } from "@angular/material/dialog";
import { filter, mergeMap, tap } from "rxjs/operators";
import { HistoryTypes } from "app/enums/ticket-history-type.enum";
import { ChatService } from "app/services/api/chat-messages.service";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { User } from "../../interfaces/i-user-user";
import { DialogFeedbakComponent } from "../dialog-feedback/dialog-feedback.component";
import { assign } from "lodash";
import { SocketService } from "app/services/socket/socket.service";
import moment from "moment";

@Component({
    standalone: false,
    selector: "app-videochat",
    templateUrl: "./videochat.component.html",
    styleUrls: ["./videochat.component.scss"],
})
export class VideochatComponent implements OnInit, OnDestroy {
    public ticket: ITicket;
    private ticketSubscription: Subscription;
    public videochatRunnable = false;
    public showRoom: String = "";
    private ticketID: number;
    private user: User;
    private isFeedbackGiven = false;
    public showIframe: boolean = false;
    public roomUrl: SafeResourceUrl;
    private boundRoomUrl = "";

    public showTimer: boolean = true;
    public timeToWarning: number;
    public timerOffset: number;
    public timer: string;
    private timerRef = moment();
    private timerStatusRef: any;

    constructor(
        private router: Router,
        private storage: LocalStorageService,
        private ticketService: ApiTicketService,
        private activeRoute: ActivatedRoute,
        private googleAnalyticsService: GoogleAnalyticsService,
        private toast: NotificationsService,
        private dialog: MatDialog,
        private chatService: ChatService,
        private socketService: SocketService,
        private sanitizer: DomSanitizer,
    ) {
        this.timeToWarning = environment.timeToWarning;
    }

    ngOnInit(): void {
        this.user = this.storage.getItem("user");

        this.googleAnalyticsService.pageEmitter("VideoChatPage");
        this.ticketID = +this.activeRoute.snapshot.paramMap.get("id");
        this.ticketSubscription = merge(
            this.ticketService.getFromId(this.ticketID),
            this.socketService.getMessage("onTicketUpdated"),
        )
            .pipe(
                filter(
                    (ticket: ITicket) => Number(ticket?.id) === this.ticketID,
                ),
            )
            .subscribe({
                next: (item: ITicket) => {
                    this.ticket = item;
                    if (this.ticket.id_status === TicketStatuses.ONLINE) {
                        this.videochatRunnable = true;
                        this.bindRoom(item);

                        this.googleAnalyticsService.eventEmitter(
                            "VideoChatPage",
                            "Init Video Session",
                        );
                    }

                    if (
                        this.ticket.id_status === TicketStatuses.CLOSED &&
                        this.isFeedbackGiven == false
                    ) {
                        this.videochatRunnable = false;
                        this.dialog
                            .open(DialogFeedbakComponent, {
                                data: {
                                    ticketId: this.ticketID,
                                },
                            })
                            .afterClosed()
                            .subscribe((res) => {
                                this.isFeedbackGiven = true;
                                this.dialog.closeAll();
                                this.router.navigate(["home"]);
                            });
                    }
                },
                error: (err) => {
                    console.error(err);
                    this.router.navigate(["home"]);
                },
            });

        this.runVideochat();
    }

    private bindRoom(item: ITicket): void {
        const room = item.user_room_url ?? "";
        if (!/^https?:\/\//i.test(room) || room === this.boundRoomUrl) {
            return;
        }
        this.boundRoomUrl = room;
        const separator = room.includes("?") ? "&" : "?";
        this.roomUrl = this.sanitizer.bypassSecurityTrustResourceUrl(
            room +
                separator +
                "logo=off&participantCount=off&displayName=" +
                encodeURIComponent(this.user.userdata.name),
        );
    }

    public runVideochat = () => {
        this.showIframe = true;
        if (!this.timerStatusRef) {
            this.timerRef = moment();
            this.timerStatusRef = setInterval(() => {
                this.timerOffset = moment().diff(this.timerRef);
                this.timer = moment.utc(this.timerOffset).format("mm:ss");
            }, 1000);
        }
    };

    exit(): void {
        this.dialog
            .open(DialogConfirm, {
                data: {
                    msg: "Sei sicuro di voler chiudere la conversazione?",
                },
            })
            .afterClosed()
            .pipe(
                filter((data) => !!data),
                mergeMap(() => {
                    this.sendHistorySystem("Ticket chiuso dall'utente");
                    const ticket: ITicket = assign({}, this.ticket, {
                        id_status: TicketStatuses.CLOSED,
                    });
                    return of(ticket);
                }),
            )
            .subscribe({
                next: (data) => {
                    this.isFeedbackGiven = true;
                    this.dialog
                        .open(DialogFeedbakComponent, {
                            data: {
                                ticketId: this.ticketID,
                            },
                        })
                        .afterClosed()
                        .subscribe((res) => {
                            this.isFeedbackGiven = true;
                            this.dialog.closeAll();
                            this.router.navigate(["home"]);
                        });
                },
                error: (err) => {
                    console.error(err);
                    this.toast.error(
                        "Nuovo Ticket",
                        "Richiesta di annullamento non è andata a buon fine!",
                    );
                },
            });
    }

    private sendHistorySystem(msg: string): void {
        const sysHistory = {
            id: null,
            id_type: HistoryTypes.SYSTEM,
            action: msg,
            id_ticket: this.ticketID,
            readed: 0,
        };
        this.chatService.sendMessage(sysHistory).subscribe();
    }

    ngOnDestroy(): void {
        if (this.ticketSubscription) {
            this.ticketSubscription.unsubscribe();
        }
    }
}
